(function () {
  if (window.ThemeCartDrawer) return;

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('.cart-drawer__panel');
  const title = drawer.querySelector('#CartDrawerTitle');
  const status = drawer.querySelector('[data-cart-drawer-status]');
  const itemsContainer = drawer.querySelector('[data-cart-drawer-items]');
  const emptyState = drawer.querySelector('[data-cart-drawer-empty]');
  const footer = drawer.querySelector('[data-cart-drawer-footer]');
  const subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
  const rootUrl = drawer.dataset.rootUrl || window.Shopify?.routes?.root || '/';
  const routesRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
  const endpoints = {
    add: `${routesRoot}cart/add.js`,
    cart: `${routesRoot}cart.js`,
    change: `${routesRoot}cart/change.js`,
  };
  const labels = {
    title: drawer.dataset.titleLabel || 'Your cart',
    added: drawer.dataset.addedLabel || 'Added to cart',
    empty: drawer.dataset.emptyLabel || 'Your cart is empty',
    emptyText: drawer.dataset.emptyText || '',
    error: drawer.dataset.errorLabel || 'Could not update cart. Please try again.',
    loading: drawer.dataset.loadingLabel || 'Updating cart...',
    subtotal: drawer.dataset.subtotalLabel || 'Subtotal',
    quantity: drawer.dataset.quantityLabel || 'Quantity',
    increase: drawer.dataset.increaseLabel || 'Increase quantity',
    decrease: drawer.dataset.decreaseLabel || 'Decrease quantity',
    remove: drawer.dataset.removeLabel || 'Remove',
  };
  let lastFocusedElement = null;
  let closeTimer = null;
  let activeRequest = Promise.resolve();
  const canUseFetch = typeof window.fetch === 'function';

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const normalizeImageUrl = (url, width = 180) => {
    if (!url) return '';

    const normalized = url.startsWith('//') ? `${window.location.protocol}${url}` : url;
    const separator = normalized.includes('?') ? '&' : '?';
    return `${normalized}${separator}width=${width}`;
  };

  const publicProperties = (properties) => {
    if (!properties || Array.isArray(properties)) return [];

    return Object.entries(properties).filter(([name, value]) => (
      value !== null &&
      value !== '' &&
      !String(name).startsWith('_')
    ));
  };

  const formatMoney = (amount, currency) => {
    const activeCurrency = currency || drawer.dataset.currency || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: activeCurrency,
      }).format(Number(amount || 0) / 100);
    } catch (error) {
      return `${activeCurrency} ${(Number(amount || 0) / 100).toFixed(2)}`;
    }
  };

  const parseJson = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const serializeForm = (form) => {
    const pairs = [];

    Array.from(form.elements || []).forEach((field) => {
      if (!field.name || field.disabled) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      if (field.type === 'submit' || field.type === 'button' || field.type === 'file') return;

      if (field.tagName === 'SELECT' && field.multiple) {
        Array.from(field.options).forEach((option) => {
          if (option.selected) {
            pairs.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(option.value)}`);
          }
        });
        return;
      }

      pairs.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`);
    });

    return pairs.join('&').replace(/%20/g, '+');
  };

  const requestJson = (url, options = {}) => {
    const method = options.method || 'GET';
    const headers = options.headers || {};

    if (canUseFetch) {
      return window.fetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        body: options.body,
      }).then(async (response) => ({
        ok: response.ok,
        status: response.status,
        payload: await response.json().catch(() => null),
      }));
    }

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();

      request.open(method, url, true);
      request.setRequestHeader('Accept', 'application/json');

      Object.entries(headers).forEach(([name, value]) => {
        request.setRequestHeader(name, value);
      });

      request.onload = () => {
        resolve({
          ok: request.status >= 200 && request.status < 300,
          status: request.status,
          payload: parseJson(request.responseText),
        });
      };

      request.onerror = () => {
        reject(new Error(labels.error));
      };

      request.send(options.body || null);
    });
  };

  const setStatus = (message = '', isError = false, isLoading = false) => {
    if (!status) return;

    status.textContent = message;
    status.classList.toggle('is-error', Boolean(isError));
    status.classList.toggle('is-loading', Boolean(isLoading));
  };

  const setUpdating = (isUpdating) => {
    drawer.classList.toggle('is-updating', isUpdating);
    drawer.setAttribute('aria-busy', isUpdating ? 'true' : 'false');
    drawer.querySelectorAll('button, a').forEach((control) => {
      if (control.matches('.cart-drawer__close')) return;

      if (control.tagName === 'BUTTON') {
        control.disabled = isUpdating;
      } else {
        control.toggleAttribute('aria-disabled', isUpdating);
      }
    });
  };

  const setTriggerExpanded = (isExpanded) => {
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  };

  const openDrawer = (heading = labels.title) => {
    window.clearTimeout(closeTimer);
    lastFocusedElement = document.activeElement;

    if (title) {
      title.textContent = heading;
    }

    drawer.hidden = false;
    document.documentElement.classList.add('cart-drawer-open');
    drawer.offsetHeight;
    drawer.classList.add('is-open');
    setTriggerExpanded(true);
    panel?.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
    panel?.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    panel?.style.removeProperty('transform');
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('cart-drawer-open');
    setTriggerExpanded(false);

    if (window.matchMedia?.('(max-width: 760px)').matches) {
      drawer.hidden = true;
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }
      return;
    }

    closeTimer = window.setTimeout(() => {
      drawer.hidden = true;
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }
    }, 230);
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  };

  const renderLineItem = (item, currency) => {
    const image = normalizeImageUrl(item.image);
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-drawer__variant">${escapeHtml(item.variant_title)}</p>`
      : '';
    const properties = publicProperties(item.properties)
      .map(([name, value]) => (
        `<p class="cart-drawer__property">${escapeHtml(name)}: ${escapeHtml(value)}</p>`
      ))
      .join('');
    const media = image
      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : '<span class="cart-drawer__placeholder" aria-hidden="true"></span>';
    const titleText = escapeHtml(item.product_title);
    const quantity = Number(item.quantity || 1);

    return `
      <article class="cart-drawer__line" data-cart-drawer-line data-key="${escapeHtml(item.key)}">
        <a class="cart-drawer__media" href="${escapeHtml(item.url || '#')}" tabindex="-1">
          ${media}
        </a>

        <div class="cart-drawer__line-info">
          <a class="cart-drawer__line-title" href="${escapeHtml(item.url || '#')}">${titleText}</a>
          ${variantTitle}
          ${properties}
          <div class="cart-drawer__line-price">${formatMoney(item.final_line_price, currency)}</div>

          <div class="cart-drawer__quantity-row">
            <div class="cart-drawer__quantity" aria-label="${escapeHtml(labels.quantity)}">
              <button type="button" data-cart-drawer-quantity="-1" aria-label="${escapeHtml(labels.decrease)}">-</button>
              <span>${quantity}</span>
              <button type="button" data-cart-drawer-quantity="1" aria-label="${escapeHtml(labels.increase)}">+</button>
            </div>

            <button class="cart-drawer__remove" type="button" data-cart-drawer-remove>
              ${escapeHtml(labels.remove)}
            </button>
          </div>
        </div>
      </article>
    `;
  };

  const renderCart = (cart) => {
    const itemCount = Number(cart.item_count || 0);
    const currency = cart.currency || drawer.dataset.currency;

    updateCartCount(itemCount);

    if (itemsContainer) {
      itemsContainer.innerHTML = itemCount
        ? cart.items.map((item) => renderLineItem(item, currency)).join('')
        : '';
    }

    if (emptyState) {
      emptyState.hidden = itemCount > 0;
    }

    if (footer) {
      footer.hidden = itemCount === 0;
    }

    if (subtotal) {
      subtotal.textContent = formatMoney(cart.items_subtotal_price, currency);
    }
  };

  const getCart = async () => {
    const response = await requestJson(endpoints.cart, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok || !response.payload) {
      throw new Error(labels.error);
    }

    return response.payload;
  };

  const updateLine = async (key, quantity) => {
    const response = await requestJson(endpoints.change, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity,
      }),
    });

    if (!response.ok || !response.payload) {
      const payload = response.payload;
      throw new Error(payload?.description || labels.error);
    }

    return response.payload;
  };

  const addFormToCart = async (form) => {
    const formBody = serializeForm(form);
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    };
    const response = await requestJson(endpoints.add, {
      method: 'POST',
      headers,
      body: formBody,
    });

    if (!response.ok || !response.payload) {
      const payload = response.payload;
      throw new Error(payload?.description || payload?.message || labels.error);
    }

    return response.payload;
  };

  const queueCartUpdate = (task) => {
    activeRequest = activeRequest
      .catch(() => {})
      .then(task);

    return activeRequest;
  };

  const syncCart = async (heading = labels.title) => {
    const cart = await getCart();
    renderCart(cart);
    openDrawer(heading);
    return cart;
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (!trigger || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    setUpdating(true);
    setStatus(labels.loading, false, true);
    openDrawer(labels.title);

    try {
      await syncCart(labels.title);
      setStatus('');
    } catch (error) {
      window.location.href = trigger.href;
    } finally {
      setUpdating(false);
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form');
    if (!form || event.defaultPrevented) return;

    const actionLink = document.createElement('a');
    actionLink.href = form.getAttribute('action') || window.location.href;
    const actionPath = actionLink.pathname;
    if (!actionPath.endsWith('/cart/add')) return;

    if (event.submitter?.closest('.shopify-payment-button')) return;

    event.preventDefault();

    const submitter = event.submitter || form.querySelector('[type="submit"]');
    submitter?.setAttribute('aria-busy', 'true');
    submitter?.setAttribute('disabled', '');
    setUpdating(true);
    setStatus(labels.loading, false, true);
    openDrawer(labels.added);

    try {
      await addFormToCart(form);

      const quickViewDialog = form.closest('[data-quick-view-dialog]');
      if (quickViewDialog && window.ThemeQuickView) {
        window.ThemeQuickView.close(quickViewDialog);
      }

      await syncCart(labels.added);
      setStatus('');
    } catch (error) {
      setStatus(error.message || labels.error, true);
      await syncCart(labels.title).catch(() => openDrawer(labels.title));
    } finally {
      submitter?.removeAttribute('aria-busy');
      submitter?.removeAttribute('disabled');
      setUpdating(false);
    }
  });

  drawer.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-cart-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const line = event.target.closest('[data-cart-drawer-line]');
    if (!line) return;

    const quantityButton = event.target.closest('[data-cart-drawer-quantity]');
    const removeButton = event.target.closest('[data-cart-drawer-remove]');

    if (!quantityButton && !removeButton) return;
    if (drawer.classList.contains('is-updating')) return;

    event.preventDefault();

    const key = line.dataset.key;
    const currentQuantity = Number(line.querySelector('.cart-drawer__quantity span')?.textContent || 1);
    const nextQuantity = removeButton
      ? 0
      : Math.max(0, currentQuantity + Number(quantityButton.dataset.cartDrawerQuantity || 0));
    const quantityText = line.querySelector('.cart-drawer__quantity span');

    if (quantityText && nextQuantity > 0) {
      quantityText.textContent = String(nextQuantity);
    }

    line.classList.toggle('is-removing', nextQuantity === 0);

    setUpdating(true);
    setStatus(labels.loading, false, true);

    queueCartUpdate(async () => {
      try {
        const cart = await updateLine(key, nextQuantity);
        renderCart(cart);
        setStatus('');
      } catch (error) {
        if (quantityText) {
          quantityText.textContent = String(currentQuantity);
        }
        line.classList.remove('is-removing');
        setStatus(error.message || labels.error, true);
      } finally {
        setUpdating(false);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  document.addEventListener('theme:cart:updated', (event) => {
    if (event.detail?.cart) {
      renderCart(event.detail.cart);
    }
  });

  const refreshCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  };

  window.ThemeCartDrawer = {
    open: () => syncCart(labels.title),
    close: closeDrawer,
    refresh: refreshCart,
  };
})();
